import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import type { AppState } from '@zerops/zerops/app';
import { ZefForm, ZefFormsManagerService } from '@zerops/zef/forms';
import { box, Boxed, FormGroupState, FormState, setErrors } from 'ngrx-forms';
import { IntegrationTokenProjectAccess, IntegrationTokenRoleCode } from '@zerops/models/integration-token';

export const DELEGATION_CREATE_FORM_NAME =
  '@zerops/zerops/feature/token-add-edit-dialog/delegation-create';

// The human create UI reuses the integration-token permission components, so it
// works with the same three-role model. Delegations created by the backend (ZCP)
// may use the full five-role range — those are only ever displayed, never edited.
export interface DelegationCreateFormValue {
  roleCode: IntegrationTokenRoleCode;
  canCreateProjects: boolean;
  projects: Boxed<IntegrationTokenProjectAccess[]>;
}

export type DelegationCreateFormState = FormState<DelegationCreateFormValue>;

// Mirrors the token rule: a NO_ACCESS delegation must grant at least one project,
// unless it can create projects (then an empty scope is still meaningful).
function validateDelegationProjects(
  roleCode: string | undefined,
  canCreateProjects: boolean | undefined,
  projects: IntegrationTokenProjectAccess[]
): { required: { actual: any } } | null {
  if (roleCode === IntegrationTokenRoleCode.NoAccess && projects.length === 0 && !canCreateProjects) {
    return { required: { actual: projects } };
  }
  return null;
}

@Injectable({ providedIn: 'root' })
export class DelegationCreateForm extends ZefForm<DelegationCreateFormValue> {
  constructor(
    public store: Store<AppState>,
    public formsManager: ZefFormsManagerService
  ) {
    super(
      store,
      formsManager,
      DELEGATION_CREATE_FORM_NAME,
      {
        roleCode: IntegrationTokenRoleCode.Admin,
        canCreateProjects: false,
        projects: box([])
      },
      {
        projects: (s, ps: FormGroupState<DelegationCreateFormValue>) => {
          const errors = validateDelegationProjects(
            ps?.controls?.roleCode.value,
            ps?.controls?.canCreateProjects.value,
            s.value?.value || []
          );
          return setErrors(s, errors || {});
        }
      }
    );
  }
}
